import { create } from "zustand";
import { persist } from "zustand/middleware";

import { lockPageScroll, unlockPageScroll } from "@/utils";

export type CareplanCartItem = {
  id: string;
  name: string;
  shortName: string;
  price: number;
  priceAsString: string;
  originalPriceAsString: string;
  /** Care plan price per unit: list minus the selling plan and care plan discounts. */
  unitPrice: number;
  unitPriceAsString: string;
  /** List price per unit, shown struck through next to `unitPrice`. */
  unitListPrice: number;
  unitListPriceAsString: string;
  image: string;
  quantity: number;
  variantId?: string;
  merchandiseId: string;
  sellingPlanId?: number | null;
  sku?: string | null;
};

export type CartState = {
  recommendedCartLines: CareplanCartItem[] | null;
  setRecommendedCartLines: (recommendedCartLines: CareplanCartItem[] | null) => void;
  showCart: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  subscriptionProcessingDeadline: number;
  setSubscriptionProcessingDeadline: (deadline: number) => void;
};

export const isCartProcessing = (state: CartState) => {
  return state.subscriptionProcessingDeadline > Date.now();
};

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => {
      return {
        recommendedCartLines: null,
        setRecommendedCartLines: (recommendedCartLines: CareplanCartItem[] | null) => {
          set(() => {
            return { recommendedCartLines };
          });
        },
        showCart: false,
        openCart: () => {
          lockPageScroll();
          set({ showCart: true });
        },
        closeCart: () => {
          unlockPageScroll();
          set({ showCart: false });
        },
        toggleCart: () => {
          const { showCart } = get();
          if (showCart) {
            unlockPageScroll();
          } else {
            lockPageScroll();
          }
          set({ showCart: !showCart });
        },
        subscriptionProcessingDeadline: 0,
        setSubscriptionProcessingDeadline: (deadline: number) => {
          const normalizedDeadline = Number.isFinite(deadline) && deadline >= 0 ? deadline : 0;

          set({ subscriptionProcessingDeadline: normalizedDeadline });
        },
      };
    },
    {
      name: "cartStore",
      partialize: (state) => {
        return { recommendedCartLines: state.recommendedCartLines };
      },
    },
  ),
);

export const getCartStoreState = () => {
  return useCartStore.getState();
};
